import { ApplicationInsightsService } from './applicationInsights';
import config from '../config';

class MobileTelemetryService {
  constructor() {
    this.queue = [];
    this.isMobile = false;
    this.batchSize = 1; // Send immediately
    this.batchInterval = 1000; // 1 second for safety
    // Use the correct backend API endpoint
    // Fallback to production API if config is not available
    const baseUrl = config?.apiEndpoint || config?.apiFunction?.endpoint || 'https://izzygo-prodfunc.azurewebsites.net';
    this.apiEndpoint = `${baseUrl}/api/telemetry`;
  }

  initialize() {
    try {
      // Detect if running on mobile
      const ua = navigator.userAgent;
      this.isMobile = /TeamsMobile|Android.*Teams|iPhone.*Teams|iPad.*Teams/i.test(ua);
      
      if (this.isMobile) {
        console.log('📱 Mobile telemetry service initialized');
        // Start batch processing
        this.startBatchProcessor();
      }
    } catch (error) {
      // Fail silently - don't break the app
      console.error('Mobile telemetry init error:', error);
      this.isMobile = false;
    }
  }

  // Send telemetry through your own backend
  async sendTelemetry(eventName, properties = {}) {
    if (!this.isMobile) {
      console.log(`📱 [MobileTelemetry] Not mobile, skipping: ${eventName}`);
      return false;
    }

    const telemetryData = {
      eventName,
      properties: {
        ...properties,
        timestamp: new Date().toISOString(),
        platform: ApplicationInsightsService.detectPlatformFromUA(navigator.userAgent),
        userAgent: navigator.userAgent,
        url: window.location.href,
        queuedAt: new Date().toISOString()
      }
    };

    console.log(`📱 [MobileTelemetry] Queuing event: ${eventName}`, telemetryData);
    this.queue.push(telemetryData);

    // Send immediately if queue is full
    if (this.queue.length >= this.batchSize) {
      console.log(`📱 [MobileTelemetry] Batch size reached, flushing immediately`);
      await this.flush();
    }

    return true;
  }

  async flush() {
    if (this.queue.length === 0) return;

    const batch = [...this.queue];
    this.queue = [];
    
    console.log(`📱 [MobileTelemetry] Flushing ${batch.length} events to ${this.apiEndpoint}`);
    console.log(`📱 [MobileTelemetry] Events:`, batch);

    // TEMPORARILY DISABLED: Telemetry endpoint doesn't exist, causing network errors
    // TODO: Re-enable when /api/telemetry endpoint is implemented
    return;
    
    try {
      // Send to your backend which will forward to App Insights
      const startTime = Date.now();
      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ events: batch })
      });

      const duration = Date.now() - startTime;
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ [MobileTelemetry] Failed: ${response.status} in ${duration}ms`);
        console.error(`❌ [MobileTelemetry] Error:`, errorText);
        // Re-queue failed events
        this.queue.unshift(...batch);
      } else {
        const result = await response.json();
        console.log(`✅ [MobileTelemetry] Success in ${duration}ms:`, result);
      }
    } catch (error) {
      console.error('❌ [MobileTelemetry] Network error:', error);
      console.error('❌ [MobileTelemetry] Failed to send to:', this.apiEndpoint);
      // Re-queue failed events
      this.queue.unshift(...batch);
    }
  }

  startBatchProcessor() {
    setInterval(() => {
      this.flush();
    }, this.batchInterval);

    // Flush on page unload
    window.addEventListener('beforeunload', () => {
      this.flush();
    });
  }

  // Track authentication events for mobile
  trackAuthEvent(action, details = {}) {
    return this.sendTelemetry(`Auth_${action}`, {
      category: 'Authentication',
      ...details
    });
  }

  // Track user actions for mobile
  trackUserAction(action, details = {}) {
    return this.sendTelemetry(`UserAction_${action}`, {
      category: 'UserInteraction',
      ...details
    });
  }

  // Track exceptions for mobile
  trackException(error, details = {}) {
    return this.sendTelemetry('Exception', {
      error: error.message || 'Unknown error',
      stack: error.stack,
      ...details
    });
  }
}

// Create singleton instance
const mobileTelemetryService = new MobileTelemetryService();
export default mobileTelemetryService;